/**
 * PsychoCohort — admin dashboard showing aggregate psychometric insights.
 *
 * URL: /dashboard/psycho-cohort
 *
 * Shows:
 *   - Overall reports + founders covered + median duration
 *   - Big-5 OCEAN averages across the cohort
 *   - Per-industry breakdown table (with industry filter)
 *   - Top flags (frequency-sorted)
 *   - OCEAN distribution histograms
 *   - Weekly time-series (12 weeks)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, ArrowLeft, RefreshCw, Users, FileText, AlertTriangle, Clock,
  TrendingUp, X, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const BIG5_KEYS = [
  ['openness', 'Openness'],
  ['conscientiousness', 'Conscientiousness'],
  ['extraversion', 'Extraversion'],
  ['agreeableness', 'Agreeableness'],
  ['neuroticism', 'Neuroticism'],
];

function Bar({ value, max = 100, hue = '#8B3FC5', height = 'h-2' }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className={`${height} rounded-full bg-slate-100 overflow-hidden`}>
      <div className="h-full" style={{ width: `${pct}%`, background: hue, transition: 'width 0.8s' }}/>
    </div>
  );
}

function Histogram({ buckets }) {
  if (!buckets || buckets.length === 0) {
    return <div className="text-[10px] text-slate-400 italic">No data</div>;
  }
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <div className="flex items-end gap-0.5 h-12">
      {buckets.map((b, i) => (
        <div
          key={i}
          className="flex-1 bg-[#8B3FC5]/30 hover:bg-[#8B3FC5] transition-colors rounded-t"
          style={{ height: `${(b.count / max) * 100}%`, minHeight: '2px' }}
          title={`${b.range}: ${b.count} reports`}
        />
      ))}
    </div>
  );
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + drill-down effects. */
export default function PsychoCohort() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState('all');
  const [drill, setDrill] = useState(null);       // { text, count, rows[], loading }
  const isAdmin = user?.role === 'system_admin' || user?.role === 'holding_admin';

  const load = async () => {
    setLoading(true);
    try {
      const params = industry === 'all' ? {} : { industry };
      const { data } = await api.get('/psycho-cohort/', { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load cohort data.');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); }, [industry, isAdmin]);

  const openFlagDrill = async (flagText) => {
    setDrill({ text: flagText, rows: [], count: 0, loading: true });
    try {
      const { data } = await api.get('/psycho-cohort/founders-by-flag', {
        params: { text: flagText },
      });
      setDrill({ text: data.flag_text, rows: data.rows, count: data.count, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load drill-down.');
      setDrill(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="ve-card p-10 text-center">
        <h2 className="display text-2xl">Restricted</h2>
        <p className="text-sm text-slate-600 mt-2">Cohort analytics is for system or holding admins only.</p>
      </div>
    );
  }
  if (loading || !data) return <div className="text-sm text-slate-500">Crunching cohort numbers…</div>;

  const o = data.overall;
  return (
    <div data-testid="psycho-cohort">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Link to="/dashboard/founders" className="ve-btn-ghost text-xs">
            <ArrowLeft size={12}/> Back to founders
          </Link>
          <div className="ve-eyebrow mt-3">Psychometric · Cohort Analytics</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            How does your <span className="ve-gold-text">founder bench</span><br/>
            actually look?
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Aggregate insights across all psychometric reports — Big-Five averages, industry breakdowns, common red flags, and submission patterns. Read your portfolio&rsquo;s personality at a glance.
          </p>
        </div>
        <button onClick={load} className="ve-btn-secondary text-xs self-start"
                data-testid="cohort-refresh">
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      {/* Industry filter */}
      {data.industries?.length > 1 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="cohort-industry-filter">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Filter by industry:</span>
          <button onClick={() => setIndustry('all')}
                  className={`text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded border ${
                    industry === 'all'
                      ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'
                  }`}>All</button>
          {data.industries.map((ind) => (
            <button key={ind}
                    onClick={() => setIndustry(ind)}
                    data-testid={`cohort-industry-${ind}`}
                    className={`text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded border ${
                      industry === ind
                        ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'
                    }`}>
              {ind}
            </button>
          ))}
        </div>
      )}

      {/* KPI strip */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="ve-card p-4" data-testid="cohort-kpi-reports">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            <FileText size={11}/> Total reports
          </div>
          <div className="display text-3xl text-[#8B3FC5] font-bold mt-2">{o.total_reports}</div>
        </div>
        <div className="ve-card p-4" data-testid="cohort-kpi-founders">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            <Users size={11}/> Founders
          </div>
          <div className="display text-3xl text-[#8B3FC5] font-bold mt-2">{o.founders_covered}</div>
        </div>
        <div className="ve-card p-4" data-testid="cohort-kpi-leadership">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            <TrendingUp size={11}/> Avg leadership
          </div>
          <div className="display text-3xl text-[#8B3FC5] font-bold mt-2">{o.leadership_avg}</div>
        </div>
        <div className="ve-card p-4" data-testid="cohort-kpi-duration">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            <Clock size={11}/> Avg duration
          </div>
          <div className="display text-3xl text-[#8B3FC5] font-bold mt-2">
            {o.duration_avg_seconds
              ? `${Math.floor(o.duration_avg_seconds / 60)}m ${Math.floor(o.duration_avg_seconds % 60)}s`
              : '—'}
          </div>
        </div>
      </div>

      {/* Big-5 average + histograms */}
      <div className="mt-6 ve-card p-5" data-testid="cohort-big5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="display text-lg font-bold">Big-Five OCEAN averages</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            risk avg · {o.risk_avg}
          </span>
        </div>
        <div className="mt-4 space-y-4">
          {BIG5_KEYS.map(([k, label]) => (
            <div key={k} className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3 font-bold text-sm text-slate-700">{label}</div>
              <div className="col-span-6 space-y-1">
                <Bar value={o.big_five_avg[k] || 0}/>
                <Histogram buckets={o.big_five_histograms[k] || []}/>
              </div>
              <div className="col-span-3 flex items-baseline gap-2 justify-end">
                <span className="display text-2xl text-[#8B3FC5] font-bold tabular-nums">{o.big_five_avg[k] || 0}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">avg</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-industry breakdown */}
      {data.per_industry?.length > 0 && (
        <div className="mt-6 ve-card p-5" data-testid="cohort-by-industry">
          <h2 className="display text-lg font-bold">Breakdown by industry</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold border-b border-slate-200">
                  <th className="text-left py-2">Industry</th>
                  <th className="text-right py-2">Reports</th>
                  <th className="text-right py-2">O</th>
                  <th className="text-right py-2">C</th>
                  <th className="text-right py-2">E</th>
                  <th className="text-right py-2">A</th>
                  <th className="text-right py-2">N</th>
                  <th className="text-right py-2">Leadership</th>
                  <th className="text-right py-2">Risk</th>
                </tr>
              </thead>
              <tbody>
                {data.per_industry.map((i, idx) => (
                  <tr key={i.industry || idx}
                      data-testid={`cohort-industry-row-${i.industry}`}
                      className="border-b border-slate-100 hover:bg-slate-50/40">
                    <td className="py-2 font-bold">{i.industry || 'Other'}</td>
                    <td className="text-right py-2 font-mono tabular-nums">{i.count}</td>
                    <td className="text-right py-2 tabular-nums">{i.big_five_avg.openness}</td>
                    <td className="text-right py-2 tabular-nums">{i.big_five_avg.conscientiousness}</td>
                    <td className="text-right py-2 tabular-nums">{i.big_five_avg.extraversion}</td>
                    <td className="text-right py-2 tabular-nums">{i.big_five_avg.agreeableness}</td>
                    <td className="text-right py-2 tabular-nums">{i.big_five_avg.neuroticism}</td>
                    <td className="text-right py-2 font-bold text-[#5E1F8B] tabular-nums">{i.leadership_avg}</td>
                    <td className="text-right py-2 tabular-nums">{i.risk_avg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top flags */}
      {o.top_flags?.length > 0 && (
        <div className="mt-6 ve-card p-5" data-testid="cohort-flags">
          <h2 className="display text-lg font-bold flex items-center gap-2">
            <AlertTriangle size={16} className="text-rose-600"/>
            Most common flags
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Frequency-sorted across the cohort. Click any row to see which founders carry it.
          </p>
          <div className="mt-4 space-y-2">
            {o.top_flags.map((f, i) => {
              const maxCount = o.top_flags[0].count;
              return (
                <button
                  key={i}
                  onClick={() => openFlagDrill(f.text)}
                  data-testid={`cohort-flag-row-${i}`}
                  className="w-full grid grid-cols-12 gap-3 items-center text-xs px-3 py-2 rounded border border-slate-200 hover:border-rose-300 hover:bg-rose-50/40 transition-colors text-left group"
                >
                  <div className="col-span-9 truncate flex items-center gap-2" title={f.text}>
                    <span className="truncate">{f.text}…</span>
                    <ChevronRight size={11} className="text-slate-300 group-hover:text-rose-500 transition-colors shrink-0"/>
                  </div>
                  <div className="col-span-2">
                    <Bar value={f.count} max={maxCount} hue="#EF4444"/>
                  </div>
                  <div className="col-span-1 text-right font-bold text-rose-700 tabular-nums">{f.count}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Weekly timeline */}
      {data.timeline_weekly?.length > 0 && (
        <div className="mt-6 ve-card p-5" data-testid="cohort-timeline">
          <h2 className="display text-lg font-bold">Reports submitted · last 12 weeks</h2>
          <div className="mt-4 flex items-end gap-1 h-32" data-testid="cohort-timeline-bars">
            {data.timeline_weekly.map((w) => {
              const max = Math.max(...data.timeline_weekly.map((d) => d.count), 1);
              const h = (w.count / max) * 100;
              return (
                <div key={w.weeks_ago} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="text-[10px] font-mono tabular-nums text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    {w.count}
                  </div>
                  <div
                    className="w-full bg-[#8B3FC5] rounded-t hover:bg-[#5E1F8B] transition-colors"
                    style={{ height: `${h}%`, minHeight: w.count > 0 ? '4px' : '0' }}
                    title={`${w.weeks_ago === 0 ? 'this week' : `${w.weeks_ago} weeks ago`}: ${w.count} reports`}
                  />
                  <div className="text-[8.5px] uppercase tracking-[0.15em] text-slate-400 font-bold">
                    {w.weeks_ago === 0 ? 'now' : `-${w.weeks_ago}w`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 text-[10px] uppercase tracking-[0.18em] text-slate-400 text-center">
        Live from <code className="font-mono">/api/psycho-cohort/</code> · {o.total_reports} reports analysed
      </div>

      {/* Drill-down side drawer */}
      {drill && <FlagDrillPanel drill={drill} onClose={() => setDrill(null)}/>}
    </div>
  );
}

// ───────────── Flag drill-down side drawer ─────────────

function FlagDrillPanel({ drill, onClose }) {
  // Close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  return (
    <>
      {/* backdrop */}
      <div
        data-testid="flag-drill-backdrop"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-in fade-in duration-200"
      />
      {/* drawer */}
      <aside
        data-testid="flag-drill-panel"
        className="fixed right-0 top-0 bottom-0 w-full md:w-[480px] bg-white shadow-2xl z-50 overflow-y-auto animate-in slide-in-from-right duration-300"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="ve-eyebrow text-rose-700">Flag drill-down</div>
              <h3 className="display text-lg font-bold mt-1 leading-tight">
                {drill.text}<span className="text-slate-400">…</span>
              </h3>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mt-2">
                {drill.loading ? 'Loading…' : `${drill.count} founder${drill.count === 1 ? '' : 's'} carrying this flag`}
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="flag-drill-close"
              className="text-slate-400 hover:text-slate-800 p-1 rounded hover:bg-slate-100"
              aria-label="Close drill-down"
            >
              <X size={18}/>
            </button>
          </div>
        </div>

        <div className="p-5 space-y-3">
          {drill.loading && (
            <div className="text-sm text-slate-500 italic">Fetching matching reports…</div>
          )}
          {!drill.loading && drill.rows.length === 0 && (
            <div className="text-sm text-slate-500 italic" data-testid="flag-drill-empty">
              No reports match this flag — likely stale (the flag may have been edited or the report deleted).
            </div>
          )}
          {drill.rows.map((row) => (
            <div
              key={row.report_id}
              data-testid={`flag-drill-row-${row.report_id}`}
              className="ve-card p-4 hover:border-rose-300 transition-colors"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-bold text-sm">{row.founder_name || row.founder_email}</h4>
                <span className="text-[9px] uppercase tracking-[0.22em] text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200 font-bold">
                  {row.industry}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {row.startup_name && <span className="font-bold">{row.startup_name}</span>}
                {row.startup_name && row.leadership_index != null && ' · '}
                {row.leadership_index != null && <span>Lead {Math.round(row.leadership_index)}</span>}
              </div>
              <p className="mt-2 text-xs text-rose-700 italic leading-snug bg-rose-50/50 border-l-2 border-rose-300 pl-3 py-1.5">
                &quot;{row.flag_full_text}&quot;
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/dashboard/psycho-reports/${row.report_id}`}
                  data-testid={`flag-drill-report-link-${row.report_id}`}
                  className="text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded border border-[#8B3FC5] text-[#5E1F8B] hover:bg-[#FFF8E5] inline-flex items-center gap-1"
                >
                  <FileText size={11}/> Open report
                </Link>
                <Link
                  to={`/dashboard/founders/${row.founder_user_id}`}
                  data-testid={`flag-drill-founder-link-${row.report_id}`}
                  className="text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded border border-slate-200 hover:border-[#8B3FC5] text-slate-700 inline-flex items-center gap-1"
                >
                  Open timeline <ChevronRight size={11}/>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
